import { requireDocumentDef, type LineColumnDef } from '@project/contracts'
import { Button, Panel } from '../design/index.js'
import type { DocumentDetail, LineRow } from '../app/api.js'
import { asText } from './format.js'
import { LineGrid, type PickableRow } from './LineGrid.js'

/** A BOQ line marked not tendered — extra work outside the original tender. */
export const isNtLine = (l: LineRow): boolean =>
  l['section'] === 'BOQ' && asText(l['tendered']).toUpperCase() === 'NO'

/** The approved form's lines without the NT work, which has its own panel. */
export const withoutNt = (lines: readonly LineRow[]): readonly LineRow[] => {
  const nt = new Set(lines.filter(isNtLine).map((l) => asText(l['id'])))
  return lines.filter((l) => !isNtLine(l) && !(l['section'] === 'ESTIMATION' && nt.has(asText(l['parentLineId']))))
}

const project = requireDocumentDef('project')
const boqColumns: readonly LineColumnDef[] = (project.lines?.columns ?? [])
  // Every line here is non-tendered; the column would only invite a mistake.
  .filter((c) => c.key !== 'tendered' && c.key !== 'billingTermLineId')
// Flat here, so the BOQ line each estimate is under is a column again.
const estimateColumns: readonly LineColumnDef[] =
  ((project.grids ?? []).find((g) => g.section === 'ESTIMATION' && g.key === undefined)?.columns ?? [])
    .map((c) => (c.key === 'parentLineId' ? { ...c, dialogOnly: false } : c))

const maxSeq = (rows: readonly LineRow[], section: string): number =>
  rows.filter((l) => l['section'] === section).reduce((m, l) => Math.max(m, Number(l['seq']) || 0), 0)

/**
 * Non Tendered BOQ and Estimation (reference).
 *
 * On an approved project the tendered BOQ is locked, and this section stays
 * live: "+ Add BOQ" and "Save NT-BOQ" add extra work and its estimate without
 * revising the project. It is not shown on a draft, where the BOQ grid's own
 * Tendered column says the same thing.
 */
export const NtBoqPanel = ({
  doc, lines, dirty, busy, onChange, onSave,
}: {
  doc: DocumentDetail
  lines: readonly LineRow[]
  dirty: boolean
  busy: boolean
  onChange: (next: readonly LineRow[]) => void
  onSave: () => void
}) => {
  const boq = lines.filter((l) => l['section'] === 'BOQ')
  const estimates = lines.filter((l) => l['section'] === 'ESTIMATION')
  const all = [...doc.lines, ...lines]

  const parents: PickableRow[] = boq.map((l, i) => ({
    value: asText(l['id']) || `seq:BOQ:${asText(l['seq'])}`,
    label: [asText(l['boqCode']), asText(l['itemLabel']) || asText(l['description'])].filter(Boolean).join(' — ') || `NT line ${i + 1}`,
  }))

  return (
    <Panel
      n="07"
      title="Non Tendered BOQ and Estimation"
      aside={(
        <Button kind="primary" disabled={!dirty || busy} onClick={onSave}>
          {busy ? 'Saving…' : 'Save NT-BOQ'}
        </Button>
      )}
    >
      <p className="helper" style={{ marginBottom: 12 }}>
        Extra work outside the tender, added to the approved project without revising it.
        It is reconciled through Regularise BOQ.
      </p>
      <LineGrid
        columns={boqColumns}
        lines={boq}
        readOnly={false}
        section="BOQ"
        addLabel="Add BOQ"
        onChange={(next) => onChange([...next.map((l) => ({ ...l, tendered: 'NO', lineType: l['lineType'] ?? 'ITEM' })), ...estimates])}
        newRow={() => ({ seq: maxSeq(all, 'BOQ') + 1, tendered: 'NO', lineType: 'ITEM' })}
        detailTitle="BOQ Detail"
      />
      <h3 className="subhead" style={{ marginTop: 24 }}>Estimation under the NT-BOQ</h3>
      <LineGrid
        columns={estimateColumns}
        lines={estimates}
        readOnly={false}
        section="ESTIMATION"
        addLabel="Add an estimation line"
        pickable={{ parentLineId: parents }}
        onChange={(next) => onChange([...boq, ...next])}
        newRow={() => ({ seq: maxSeq(all, 'ESTIMATION') + 1 })}
        detailTitle="Estimation"
        detailAsksFirst="costType"
        detailRef="boqCode"
      />
    </Panel>
  )
}
